import { IconBuilding } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import PortalPage from "src/components/app/PortalPage/PortalPage";
import AssetSearchSettings from "src/components/app/ProjectSettings/AssetSearchSettings";
import DeliverySettings from "src/components/app/ProjectSettings/DeliverySettings";
import ImageMetadataSettings from "src/components/app/ProjectSettings/ImageMetadataSettings";
import ProjectAdminSettings from "src/components/app/ProjectSettings/ProjectAdminSettings";
import ProjectHandoverSettings from "src/components/app/ProjectSettings/ProjectHandoverSettings";
import ProjectMoveSettings from "src/components/app/ProjectSettings/ProjectMoveSettings";
import ProjectSettings from "src/components/app/ProjectSettings/ProjectSettings";
import TestInputsSettings from "src/components/app/ProjectSettings/TestInputsSettings";
import WebsiteSettings from "src/components/app/ProjectSettings/WebsiteSettings";
import Button from "src/components/common/Button/Button";
import { DeferredLoaderData } from "src/lib/LoaderData";
import { raceLoaderData } from "src/lib/LoaderResult";
import { currentOrganisationPath } from "src/lib/PortalPaths";
import { scopeFrom } from "src/lib/ScopedLoaders";
import { getSettingsPageData } from "src/server/functions/Portal";
import { useExtracted } from "#i18n";

type SettingsPageData = Awaited<ReturnType<typeof getSettingsPageData>>;

export const Route = createFileRoute("/_authed/portal/proj_{$projectKey}/settings")({
  // The page's project settings come from the portal layout; the search indexing estimate (shown
  // while search is off) and, for owners, the project's open transfer are loaded here.
  loader: async (context) => {
    const { project } = await scopeFrom(context);
    if (!project) return raceLoaderData(Promise.resolve<SettingsPageData>({}));
    return raceLoaderData(
      getSettingsPageData({
        data: {
          projectId: project.id,
          orgId: project.orgId,
          estimate: !project.settings?.aiMediaSearchEnabled,
        },
      }),
    );
  },
  component: SettingsPage,
});

function SettingsPage() {
  const translateMessage = useExtracted();
  const loaderData = Route.useLoaderData();
  return (
    <PortalPage>
      <PortalPage.Header>
        <PortalPage.Heading>
          <PortalPage.Title>{translateMessage("Project settings")}</PortalPage.Title>
          <PortalPage.Description>
            {translateMessage("Manage your project’s details and content delivery.")}
          </PortalPage.Description>
        </PortalPage.Heading>
        <PortalPage.Actions>
          <Button
            label={translateMessage("Organisation settings")}
            href={currentOrganisationPath("/settings")}
            variant="outline"
          >
            <IconBuilding size={18} aria-hidden />
            {translateMessage("Organisation settings")}
          </Button>
        </PortalPage.Actions>
      </PortalPage.Header>
      <PortalPage.Content>
        <ProjectSettings />
        <DeliverySettings />
        <ImageMetadataSettings />
        <WebsiteSettings />
        <TestInputsSettings />
        <DeferredLoaderData result={loaderData}>
          {(data) => (
            <AssetSearchSettings
              initialProjectId={data?.projectId}
              initialEstimate={data?.initialEstimate}
            />
          )}
        </DeferredLoaderData>
        <ProjectMoveSettings />
        <DeferredLoaderData result={loaderData}>
          {(data) => <ProjectHandoverSettings initialTransfers={data?.initialTransfers} />}
        </DeferredLoaderData>
        <ProjectAdminSettings />
      </PortalPage.Content>
    </PortalPage>
  );
}
